import { applyMask, buildMaskVars } from './naming.ts'
import { boxesIntersectOBB, type Box2D } from './collisionGuard.logic.ts'
import { formatNamingProfile, resolveNamingProfileVersion, type NamingProfileAssignments, type NamingProfileCatalog } from './namingProfile.logic.ts'
import type { RackNode, RowConfig } from '../store/editorStore.ts'

type ExtensionSide = 'head' | 'tail'
type FloorPlanBounds = { originXMm: number; originYMm: number; widthMm: number; heightMm: number }

export type RowExtensionPlan = {
  canApply: boolean
  additions: RackNode[]
  updates: Array<{ id: string; patch: Partial<RackNode> }>
  renumber: Array<{ rackId: string; oldAddress: string; newAddress: string }>
  nextRow: RowConfig
  errors: string[]
}

type Options = {
  row: RowConfig
  racks: RackNode[]
  allRacks: RackNode[]
  side: ExtensionSide
  count: number
  namingPattern: string
  module: string
  hall: string
  namingProfiles?: NamingProfileCatalog
  namingAssignments?: NamingProfileAssignments
  floorPlan: FloorPlanBounds
}

const id = () => Math.random().toString(36).slice(2, 10)

function address(options: Options, row: RowConfig, position: number): string {
  if (options.namingProfiles && options.namingAssignments) {
    const module = Number(options.module)
    if (Number.isInteger(module) && module > 0) {
      const profile = resolveNamingProfileVersion(options.namingProfiles, options.namingAssignments, { rowId: row.id, moduleId: row.moduleId ?? undefined })
      return formatNamingProfile(profile.profile, { module, row: row.labelIndex, rack: position })
    }
  }
  return applyMask(options.namingPattern, buildMaskVars({ module: options.module, hall: options.hall, row: row.labelIndex, rack: position }))
}

function axisOf(row: RowConfig): 'horizontal' | 'vertical' {
  return row.axis ?? (row.orientation === 90 || row.orientation === 270 ? 'vertical' : 'horizontal')
}

function validateBounds(rack: RackNode, floorPlan: FloorPlanBounds): boolean {
  const minX = floorPlan.originXMm / 1000 * 100
  const minY = floorPlan.originYMm / 1000 * 100
  const maxX = minX + floorPlan.widthMm / 1000 * 100
  const maxY = minY + floorPlan.heightMm / 1000 * 100
  return rack.x >= minX && rack.y >= minY && rack.x + rack.width <= maxX && rack.y + rack.height <= maxY
}

function asCollisionBox(rack: RackNode): Box2D {
  return { id: rack.id, x: rack.x, y: rack.y, w: rack.width, h: rack.height, rotation: rack.rotation }
}

export function previewRowExtension(options: Options): RowExtensionPlan {
  const { row, side, count } = options
  const ordered = [...options.racks].sort((a, b) => (a.rackPosInRow ?? 0) - (b.rackPosInRow ?? 0))
  const errors: string[] = []
  if (!Number.isInteger(count) || count < 1 || count > 1000) errors.push('Количество стоек должно быть от 1 до 1000')
  if (!ordered.length) errors.push('Нельзя продлить пустой ряд')
  if (errors.length) return { canApply: false, additions: [], updates: [], renumber: [], nextRow: row, errors }

  const first = ordered[0]
  const last = ordered[ordered.length - 1]
  const axis = axisOf(row)
  const sequenceSign = row.sequenceDirection === 'reverse' ? -1 : 1
  const physicalStep = axis === 'horizontal' ? (first.widthM ?? first.width / 100) * 100 : (first.widthM ?? first.width / 100) * 100
  const dx = axis === 'horizontal' ? sequenceSign * physicalStep : 0
  const dy = axis === 'vertical' ? sequenceSign * physicalStep : 0
  const extensionStart = side === 'tail'
    ? { x: last.x + dx, y: last.y + dy }
    : { x: first.x - dx * count, y: first.y - dy * count }
  const newPositionStart = side === 'tail' ? ordered.length + 1 : 1
  const positionFor = (index: number) => side === 'tail' ? newPositionStart + index : newPositionStart + index
  const additions: RackNode[] = []
  for (let index = 0; index < count; index += 1) {
    const position = positionFor(index)
    const x = extensionStart.x + dx * index
    const y = extensionStart.y + dy * index
    additions.push({
      ...first,
      id: id(), x, y, label: address(options, row, position),
      rackPosInRow: position, rackIndex: position - 1, rowIndex: row.labelIndex - 1,
      isDirty: true, dbId: undefined,
    })
  }

  const updates: RowExtensionPlan['updates'] = []
  const renumber: RowExtensionPlan['renumber'] = []
  if (side === 'head') {
    ordered.forEach(rack => {
      const oldPosition = rack.rackPosInRow ?? 1
      const newPosition = oldPosition + count
      updates.push({ id: rack.id, patch: { rackPosInRow: newPosition, rackIndex: newPosition - 1, label: address(options, row, newPosition), isDirty: true } })
      renumber.push({ rackId: rack.id, oldAddress: rack.label, newAddress: address(options, row, newPosition) })
    })
  }

  const nextRow: RowConfig = {
    ...row,
    head: side === 'head' ? { xM: (row.head?.xM ?? first.x / 100) - (axis === 'horizontal' ? dx * count / 100 : 0), yM: (row.head?.yM ?? first.y / 100) - (axis === 'vertical' ? dy * count / 100 : 0) } : row.head,
    tail: side === 'tail' ? { xM: (row.tail?.xM ?? last.x / 100) + (axis === 'horizontal' ? dx * count / 100 : 0), yM: (row.tail?.yM ?? last.y / 100) + (axis === 'vertical' ? dy * count / 100 : 0) } : row.tail,
    positions: side === 'tail'
      ? [...(row.positions ?? ordered.map(rack => ({ id: `${row.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow! }))), ...additions.map(rack => ({ id: `${row.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow! }))]
      : [
          ...additions.map(rack => ({ id: `${row.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow! })),
          ...(row.positions ?? ordered.map(rack => ({ id: `${row.id}-position-${rack.rackPosInRow}`, position: rack.rackPosInRow! }))).map(position => ({ ...position, position: position.position + count })),
        ],
  }

  const existingOther = options.allRacks.filter(rack => rack.rowId !== row.id)
  additions.forEach(rack => {
    if (!validateBounds(rack, options.floorPlan)) errors.push(`Стойка ${rack.label} выходит за границы плана`)
    if (existingOther.some(other => boxesIntersectOBB(asCollisionBox(rack), asCollisionBox(other)))) errors.push(`Стойка ${rack.label} пересекается с существующим объектом`)
  })
  if (new Set(additions.map(rack => rack.label)).size !== additions.length) errors.push('Новые адреса стоек не уникальны')
  const finalLabels = [
    ...existingOther.map(rack => rack.label),
    ...additions.map(rack => rack.label),
    ...updates.map(update => update.patch.label).filter((label): label is string => Boolean(label)),
  ].filter(Boolean).map(label => label.toLocaleLowerCase())
  if (new Set(finalLabels).size !== finalLabels.length) errors.push('Адрес стойки уже используется в другом ряду')
  return { canApply: errors.length === 0, additions, updates, renumber, nextRow, errors: [...new Set(errors)] }
}
